/**
 * Kurateret hovednavigation (ren modul, ingen React/dataadgang).
 *
 * Shopify-butikken har 200+ kollektioner — rå titler dur ikke i en nav.
 * Her kurateres et fast sæt hovedpunkter (NAV_CONFIG) med grupper af
 * {handle, label}, og `buildNav()` opløser handles mod de faktiske
 * kollektioner fra kataloget:
 *   - handles der ikke findes (eller ikke er tilgængelige for firmaet)
 *     springes over
 *   - kollektioner uden produkter springes over
 *   - et hovedpunkt forsvinder helt, hvis intet i det kan opløses
 *   - dubletter (samme kollektion to gange i ét punkt) fjernes
 *
 * `isInternalCollection()` er blokeringslisten for interne/tekniske
 * kollektioner, der ALDRIG må vises i nogen kollektionsliste.
 */

/** Minimal kollektionsform som nav'en behøver (matcher ShopifyCollection). */
export interface NavCollection {
  id: string;
  handle: string;
  title: string;
  productIds: string[];
}

export interface NavEntryConfig {
  handle: string;
  /** Kurateret dansk label; udelades → kollektionens egen titel. */
  label?: string;
}

export interface NavGroupConfig {
  /** Kolonneoverskrift i megamenuen; udeladt = flad gruppe. */
  heading?: string;
  entries: NavEntryConfig[];
}

export interface NavItemConfig {
  id: string;
  label: string;
  /** Handle for kollektionen som selve hovedpunktets label linker til. */
  linkHandle: string;
  /** Lille badge på hovedpunktet (fx "Nu" på Tilbud). */
  badge?: string;
  groups: NavGroupConfig[];
}

export interface ResolvedNavLink {
  /** Kollektionens gid (bruges i ?kollektion=). */
  collectionId: string;
  handle: string;
  label: string;
  productCount: number;
}

export interface ResolvedNavGroup {
  heading?: string;
  links: ResolvedNavLink[];
}

export interface ResolvedNavItem {
  id: string;
  label: string;
  badge?: string;
  /** Primært link (hovedpunktets label); falder tilbage til første underlink. */
  link: ResolvedNavLink;
  groups: ResolvedNavGroup[];
}

/* ------------------------------------------------------------------ */
/* Blokeringsliste                                                     */
/* ------------------------------------------------------------------ */

/** Interne/tekniske kollektioner der aldrig må vises i UI-lister. */
const BLOCKED_HANDLES = new Set([
  "best-selling-products",
  "newest-products",
  "ikke-nedsatte-vare",
  "l3d-dropship",
  "mega-menu-featured-products",
  "engros-storkob",
  "simplyprint-shop",
  "lab3d-zigbee-tryk",
  "kc3d-3d-print",
]);

/**
 * Er kollektionen intern/teknisk (må aldrig vises)? Matcher både handle
 * og titel (case-insensitivt), inkl. Shopify-dubletter ("…-kopi", "…-kopi-1").
 */
export function isInternalCollection(handleOrTitle: string): boolean {
  const v = handleOrTitle.trim().toLowerCase();
  if (BLOCKED_HANDLES.has(v)) return true;
  if (/-kopi(-\d+)?$/.test(v)) return true; // handle-dubletter
  if (/\bkopi(\s+\d+)?\)?$/.test(v)) return true; // titel-dubletter "… kopi"/"… (kopi)"
  return false;
}

/**
 * Brand-marketingserier o.l. der holdes ude af nav'en (men stadig kan nås
 * via deep-links/søgning) — bruges sammen med isInternalCollection, når
 * "øvrige kategorier" skal listes.
 */
export function isHiddenFromNav(handle: string): boolean {
  if (isInternalCollection(handle)) return true;
  const v = handle.trim().toLowerCase();
  if (/^bambu-lab-.+-serie$/.test(v)) return true; // fx bambu-lab-h2d-serie
  if (v === "bambu-printer") return true;
  if (v === "prusa-core-one-parts") return true;
  if (/^prusa-original-/.test(v)) return true; // Prusa model-kollektioner
  return false;
}

/* ------------------------------------------------------------------ */
/* Kurateret konfiguration                                             */
/* ------------------------------------------------------------------ */

export const NAV_CONFIG: NavItemConfig[] = [
  {
    id: "filament",
    label: "Filament",
    linkHandle: "filament-1",
    groups: [
      {
        heading: "Efter materiale",
        entries: [
          { handle: "pla", label: "PLA" },
          { handle: "petg", label: "PETG" },
          { handle: "abs-asa", label: "ABS & ASA" },
          { handle: "flex-tpu", label: "TPU / fleksibelt" },
          { handle: "teknisk-filament", label: "Teknisk filament" },
          { handle: "nylon-pa", label: "Nylon / PA" },
          { handle: "carbon-fiber-filament", label: "Carbon- & glasfiber" },
          { handle: "pc-filament", label: "PC (polycarbonat)" },
          { handle: "specialfilament", label: "Specialfilament" },
        ],
      },
      {
        heading: "Overflade & finish",
        entries: [
          { handle: "matte-filament", label: "Mat" },
          { handle: "silk-filament", label: "Silk" },
          { handle: "translucent-filament", label: "Translucent" },
          { handle: "glitter-filament", label: "Glitter" },
          { handle: "gradient-filament", label: "Gradient" },
          { handle: "wood-filament", label: "Træ" },
          { handle: "metal-marble-filament", label: "Metal & marmor" },
          { handle: "glow-filament", label: "Glow in the dark" },
        ],
      },
      {
        heading: "Efter mærke",
        entries: [
          { handle: "lab3d-filamnet", label: "LAB3D Filament" },
          { handle: "bambu-lab-filament", label: "Bambu Lab" },
          { handle: "fiberlogy-filament", label: "Fiberlogy" },
          { handle: "polymaker", label: "Polymaker" },
          { handle: "prusament-filament", label: "Prusament" },
        ],
      },
      {
        heading: "Andet",
        entries: [
          { handle: "filament-refill", label: "Refill-spoler" },
          { handle: "alt-filament-maengderabat", label: "Alt filament — mængderabat" },
        ],
      },
    ],
  },
  {
    id: "printere",
    label: "3D-printere",
    linkHandle: "fdm-3d-printere",
    groups: [
      {
        entries: [
          { handle: "fdm-3d-printere", label: "FDM-printere" },
          { handle: "resin-3d-printere", label: "Resin-printere" },
          { handle: "sls-3d-printere", label: "SLS-printere" },
          { handle: "bambu-lab-3d-printere", label: "Bambu Lab" },
          { handle: "original-prusa-3d-printer", label: "Prusa" },
        ],
      },
    ],
  },
  {
    id: "reservedele",
    label: "Reservedele",
    linkHandle: "reservedele",
    groups: [
      {
        heading: "Bambu Lab",
        entries: [
          { handle: "bambu-lab-reservedele", label: "Alle Bambu-dele" },
          { handle: "bambu-lab-reservedele-x1", label: "X1-serien" },
          { handle: "bambu-lab-reservedele-p1", label: "P1-serien" },
          { handle: "bambu-lab-reservedele-p2", label: "P2-serien" },
          { handle: "bambu-lab-reservedele-h2", label: "H2-serien" },
          { handle: "bambu-lab-reservedele-a1", label: "A1-serien" },
          { handle: "bambu-lab-reservedele-a1-mini", label: "A1 mini" },
          { handle: "bambu-lab-reservedele-x2", label: "X2-serien" },
          { handle: "bambu-lab-reservedele-a2", label: "A2L-serien" },
          { handle: "bambu-lab-reservedele-ams", label: "AMS" },
        ],
      },
      {
        heading: "Prusa",
        entries: [
          { handle: "prusa-mk3s-reservedele", label: "MK3/S/+" },
          { handle: "prusa-mk4-reservedele", label: "MK4" },
          { handle: "prusa-mini-reservedele", label: "MINI/+" },
          { handle: "prusa-xl-reservedele", label: "XL" },
          { handle: "prusa-core-one-reservedele", label: "CORE One" },
        ],
      },
      {
        heading: "Efter type",
        entries: [
          { handle: "hotends", label: "Hotends & heatbreaks" },
          { handle: "dyser-nozzle", label: "Dyser/nozzles" },
          { handle: "sliddele", label: "Sliddele & vedligehold" },
          { handle: "remme-lejer", label: "Remme & lejer" },
          { handle: "extrudere", label: "Extruder & drivgear" },
          { handle: "motorer", label: "Motorer" },
          { handle: "elektronik", label: "Elektronik & sensorer" },
          { handle: "paneler", label: "Paneler & kabinet" },
          { handle: "byggeplader", label: "Byggeplader" },
        ],
      },
    ],
  },
  {
    id: "tilbehoer",
    label: "Tilbehør",
    linkHandle: "vaerktoej-tilbehoer",
    groups: [
      {
        entries: [
          { handle: "vaerktoej-tilbehoer", label: "Værktøj & tilbehør" },
          { handle: "filamenttoerrer", label: "Filamenttørrer & dry box" },
          { handle: "spoler-opbevaring", label: "Spoler & opbevaring" },
          { handle: "byggeplader", label: "Byggeplader & print sheets" },
          { handle: "kameraer", label: "Kameraer & overvågning" },
          { handle: "makers-supply", label: "Maker's Supply" },
          { handle: "tilbehor-til-milwaukee-packout", label: "Milwaukee Packout" },
          { handle: "threaded-inserts", label: "Threaded inserts" },
        ],
      },
    ],
  },
  {
    id: "maskiner",
    label: "Resin & maskiner",
    linkHandle: "resin",
    groups: [
      {
        entries: [
          { handle: "resin", label: "Resin til 3D print" },
          { handle: "3d-scannere", label: "3D-scannere" },
          { handle: "laserskaerere", label: "Laserskærere" },
          { handle: "cnc-fraesere", label: "CNC-fræsere" },
          { handle: "vakuumformere", label: "Vakuumformere" },
          { handle: "rense-vaskestationer", label: "Rense- & vaskestationer" },
        ],
      },
    ],
  },
  {
    id: "tilbud",
    label: "Tilbud",
    linkHandle: "tilbud",
    badge: "Nu",
    groups: [
      {
        entries: [
          { handle: "tilbud", label: "Tilbud" },
          { handle: "nyhed", label: "Nyheder" },
          { handle: "overskud-fra-lab3ds-printfarm", label: "Overskud fra printfarmen" },
        ],
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Opløsning                                                           */
/* ------------------------------------------------------------------ */

/**
 * Opløs NAV_CONFIG mod de faktiske kollektioner.
 * @param collections     Kollektioner fra kataloget.
 * @param accessibleCollectionIds  Valgfrit: kun kollektions-id'er i dette
 *   sæt medtages (bruges til firma-adgangsfiltrering). Udeladt = alle.
 */
export function buildNav(
  collections: readonly NavCollection[],
  accessibleCollectionIds?: ReadonlySet<string>,
): ResolvedNavItem[] {
  const byHandle = new Map<string, NavCollection>();
  for (const c of collections) {
    if (!byHandle.has(c.handle)) byHandle.set(c.handle, c);
  }

  const resolve = (entry: NavEntryConfig): ResolvedNavLink | null => {
    const c = byHandle.get(entry.handle);
    if (!c) return null;
    if (isInternalCollection(c.handle) || isInternalCollection(c.title)) return null;
    if (c.productIds.length === 0) return null;
    if (accessibleCollectionIds && !accessibleCollectionIds.has(c.id)) return null;
    return {
      collectionId: c.id,
      handle: c.handle,
      label: entry.label ?? c.title,
      productCount: c.productIds.length,
    };
  };

  const items: ResolvedNavItem[] = [];
  for (const item of NAV_CONFIG) {
    const seen = new Set<string>();
    const groups: ResolvedNavGroup[] = [];
    for (const group of item.groups) {
      const links: ResolvedNavLink[] = [];
      for (const entry of group.entries) {
        const link = resolve(entry);
        if (!link || seen.has(link.collectionId)) continue;
        seen.add(link.collectionId);
        links.push(link);
      }
      if (links.length > 0) groups.push({ heading: group.heading, links });
    }

    const primary = resolve({ handle: item.linkHandle, label: item.label });
    const link = primary ?? groups[0]?.links[0] ?? null;
    if (!link) continue; // hovedpunktet forsvinder, hvis intet kan opløses
    items.push({
      id: item.id,
      label: item.label,
      badge: item.badge,
      link: { ...link, label: item.label },
      groups,
    });
  }
  return items;
}

/** Alle kollektions-id'er som ét opløst hovedpunkt refererer (primært + grupper). */
export function navItemCollectionIds(item: ResolvedNavItem): Set<string> {
  const ids = new Set<string>([item.link.collectionId]);
  for (const g of item.groups) for (const l of g.links) ids.add(l.collectionId);
  return ids;
}

/** Find det hovedpunkt, der indeholder en given kollektion (til deep-links). */
export function findNavItemForCollection(
  nav: readonly ResolvedNavItem[],
  collectionId: string,
): ResolvedNavItem | null {
  return nav.find((item) => navItemCollectionIds(item).has(collectionId)) ?? null;
}

/** Underkategorier for ét hovedpunkt: grupper fladet ud, dedupleret. */
export function flattenNavItemLinks(item: ResolvedNavItem): ResolvedNavLink[] {
  const seen = new Set<string>();
  const links: ResolvedNavLink[] = [];
  for (const g of item.groups) {
    for (const l of g.links) {
      if (seen.has(l.collectionId)) continue;
      seen.add(l.collectionId);
      links.push(l);
    }
  }
  return links;
}

/** Samtlige kollektions-id'er den kuraterede nav refererer. */
export function curatedCollectionIds(nav: readonly ResolvedNavItem[]): Set<string> {
  const ids = new Set<string>();
  for (const item of nav) for (const id of navItemCollectionIds(item)) ids.add(id);
  return ids;
}

/**
 * "Øvrige kategorier": tilgængelige kollektioner der hverken er kuraterede,
 * interne eller nav-skjulte — sorteret alfabetisk (dansk).
 */
export function otherCollections(
  collections: readonly NavCollection[],
  nav: readonly ResolvedNavItem[],
  accessibleCollectionIds?: ReadonlySet<string>,
): NavCollection[] {
  const curated = curatedCollectionIds(nav);
  return collections
    .filter(
      (c) =>
        !curated.has(c.id) &&
        c.productIds.length > 0 &&
        !isHiddenFromNav(c.handle) &&
        !isInternalCollection(c.title) &&
        (!accessibleCollectionIds || accessibleCollectionIds.has(c.id)),
    )
    .sort((a, b) => a.title.localeCompare(b.title, "da"));
}

/**
 * Kollektions-id'er med mindst ét tilladt produkt (adgangsfiltrering).
 * `allowedProductIds === null` betyder "alle produkter tilladt".
 */
export function accessibleCollectionIdSet(
  collections: readonly NavCollection[],
  allowedProductIds: ReadonlySet<string> | null,
): Set<string> {
  const ids = new Set<string>();
  for (const c of collections) {
    if (c.productIds.length === 0) continue;
    if (allowedProductIds === null || c.productIds.some((id) => allowedProductIds.has(id))) {
      ids.add(c.id);
    }
  }
  return ids;
}
